import React from "react";
import "./tournament-round-hole-assignment-team-item.scss";

import {
  GSButton,
  GSInfoGroup,
  GSLoadingSpinnerOverlay,
  useMediaQuery
} from "golfstatus_react_components";
import {faCircleXmark, faRetweet} from "@fortawesome/free-solid-svg-icons";
import {useDispatch, useSelector} from "react-redux";
import {selectCurrentTeam, selectTournamentTeams, setCurrentTeam} from "../../../../reducers/teamsSlice";
import {
  saveRoundHoleAssignment,
  selectCurrentHoleAssignment,
  selectLoading,
  selectPendingTeams,
  clearRecentlyMovedTeam,
  setCurrentHoleAssignment,
  setPendingHoleAssignment,
  setPendingTeams,
  setRecentlyAssignedTeams,
  setRecentlyUnassignedTeams,
  swapRoundHoleAssignments
} from "../../../../reducers/roundSlice";
import { selectCurrentTournament } from "../../../../reducers/tournamentSlice";
import { selectTournamentFlights } from "../../../../reducers/flightSlice";
import { displayPlayerHandicap, displayTeamHandicap } from "../../../../helpers/Handicaps";
import { getTeamFlight } from "../../../../hooks/round/roundHoleAssignmentHooks";

const TournamentRoundHoleAssignmentTeamItem = (props) => {
  const dispatch = useDispatch();
  const { isPhone } = useMediaQuery();

  const {
    team,
    holeAssignment,
    assigned,
    available,
    readOnly,
    selected,
    swappable,
    justUnassigned,
    justAssigned,
  } = props;

  // selectors
  const currentHoleAssignment = useSelector(selectCurrentHoleAssignment);
  const currentTournament = useSelector(selectCurrentTournament);
  const currentTournamentTeam = useSelector(selectCurrentTeam);
  const flights = useSelector(selectTournamentFlights);
  const pendingTeams = useSelector(selectPendingTeams);
  const roundLoading = useSelector(selectLoading);
  const tournamentTeams = useSelector(selectTournamentTeams);

  //the id the team is known by, whichever list the card is sitting in
  const teamID = team?.tournamentTeamKey ?? team?.id;

  //a spot names its team by key, so the card shows the team as the teams list has
  //it: name, players, handicaps, flight and place on the leaderboard. what was
  //handed in is the fallback for a team the list doesn't have. moving or removing
  //the team still works from the spot.
  const shownTeam =
    tournamentTeams?.find?.((t) => t?.id === teamID) ?? team;
  const scoreDetails = shownTeam?.mainLeaderboardScoreDetails;

  //an assignment is in flight and this is one of the teams it is moving
  const isMoving =
    roundLoading?.some?.((l) =>
      ["saveRoundHoleAssignment", "swapRoundHoleAssignments"].includes(l)
    ) && pendingTeams?.includes?.(teamID);

  //a spot can render before the teams list is in, while the page is still
  //loading, so a team it doesn't know yet goes without a name
  const getTeamName = () => {
    const contactName = shownTeam?.contactName;
    const possessive = contactName?.toLowerCase?.()?.endsWith?.("s") ? "'" : "'s";
    const teamName =
      shownTeam?.name ?? (contactName ? `${contactName}${possessive} Team` : "");

    return `${teamName} ${displayTeamHandicap(shownTeam, currentTournament)}`.trim();
  };

  const getPlayerName = (player) => {
    return `${player.name} ${displayPlayerHandicap(player, currentTournament)}`.trim();
  };

  const getAssignedTeam = (assignment, grouping) => {
    return assignment.roundHoleAssignmentTournamentTeams?.find((tt) => tt.grouping === grouping);
  };

  const assignTeamToHoleAssignment = (team, holeAssignment, grouping) => {
    const targetRhatt = getAssignedTeam(holeAssignment, grouping);
    const filteredRhatts = targetRhatt
      ? [...holeAssignment.roundHoleAssignmentTournamentTeams.filter((rhatt) => rhatt.id !== targetRhatt?.id)]
      : [...holeAssignment.roundHoleAssignmentTournamentTeams];
    dispatch(
      setPendingTeams([
        team?.id ?? team?.tournamentTeamKey,
        targetRhatt?.tournamentTeamKey,
      ])
    );
    dispatch(setPendingHoleAssignment({ id: holeAssignment?.id, grouping }));
    //the team taking the spot lands assigned, the one it replaces lands back in
    //the unassigned list
    dispatch(setRecentlyAssignedTeams([team?.id ?? team?.tournamentTeamKey]));
    dispatch(setRecentlyUnassignedTeams([targetRhatt?.tournamentTeamKey]));
    const updatedHoleAssignment = {
      ...holeAssignment,
      roundHoleAssignmentTournamentTeams: [
        ...filteredRhatts,
        { tournamentTeamKey: team.id, roundHoleAssignmentKey: holeAssignment.id, grouping: grouping },
      ],
    };

    dispatch(saveRoundHoleAssignment(updatedHoleAssignment));
  };

  const unassignTeam = () => {
    //removing isn't a selection gesture. a selection left standing describes the
    //spot this empties, and goes on highlighting it until the save comes back.
    dispatch(setCurrentHoleAssignment({}));
    dispatch(setCurrentTeam({}));
    //marks the card that is about to land in the unassigned list
    dispatch(setRecentlyUnassignedTeams([team?.tournamentTeamKey]));
    dispatch(setRecentlyAssignedTeams([]));
    dispatch(setPendingTeams([team?.tournamentTeamKey]));
    dispatch(setPendingHoleAssignment({}));
    dispatch(saveRoundHoleAssignment({
      ...holeAssignment,
      roundHoleAssignmentTournamentTeams: holeAssignment.roundHoleAssignmentTournamentTeams.filter((rhatt) => rhatt.id !== team.id),
    }));
  };

  const swapTeam = () => {
    if (currentTournamentTeam?.id !== undefined) {
      assignTeamToHoleAssignment(currentTournamentTeam, holeAssignment, team.grouping);
    } else if (currentHoleAssignment?.id !== undefined) {
      if (holeAssignment?.id !== undefined) {
        dispatch(
          setPendingTeams([
            getAssignedTeam(currentHoleAssignment, currentHoleAssignment?.grouping)
              ?.tournamentTeamKey,
            team?.tournamentTeamKey,
          ])
        );
        //the spot this team is headed for only reads as pending while it is
        //empty. two assigned teams trading places have no spot to fill.
        const destination = getAssignedTeam(
          currentHoleAssignment,
          currentHoleAssignment?.grouping
        )
          ? {}
          : {
              id: currentHoleAssignment?.id,
              grouping: currentHoleAssignment?.grouping,
            };
        dispatch(setPendingHoleAssignment(destination));
        dispatch(
          setRecentlyAssignedTeams([
            getAssignedTeam(currentHoleAssignment, currentHoleAssignment?.grouping)
              ?.tournamentTeamKey,
            team?.tournamentTeamKey,
          ])
        );
        dispatch(setRecentlyUnassignedTeams([]));
        dispatch(swapRoundHoleAssignments({ assignments: [currentHoleAssignment, {...holeAssignment, grouping: team.grouping}] }));
      } else {
        assignTeamToHoleAssignment(team, currentHoleAssignment, currentHoleAssignment.grouping);
      }
    }
  };

  //a disqualified team's score comes through as its reason (DQ, WD, etc.) rather
  //than a number. a team with no score yet, as every team has before the event,
  //hasn't been disqualified, and keeps showing its flight.
  const teamIsDisqualified = () => {
    const totalScore = scoreDetails?.totalScore;
    return (
      totalScore !== undefined &&
      totalScore !== null &&
      `${totalScore}`.trim() !== "" &&
      isNaN(Number(totalScore))
    );
  };

  const getDisqualificationReason = () => {
    if (teamIsDisqualified()) {
      return scoreDetails?.totalScore;
    }
  };

  const contactInfo = {
    sectionItems: [
      {
        type: "primary full-width capitalize",
        value: getTeamName(),
      },
      {
        type: "secondary full-width capitalize",
        value: shownTeam.players
          ?.map?.((p) => getPlayerName(p))
          ?.join?.(", "),
      },
    ],
  };

  const playerInfo = {
    sectionItems: [
      {
        type: "primary full-width capitalize",
        value: shownTeam.players
          ?.map?.((p) => getPlayerName(p)),
      },
    ],
  };

  //teams in a pre-flighted event are expected to have a flight before it starts
  const isUnflighted = (flight) => {
    return currentTournament?.preflight && !flight;
  };

  const getFlightPositionInfo = () => {
    const flight = getTeamFlight(shownTeam, flights);
    let flightPosition = ""

    if (teamIsDisqualified()) {
      flightPosition = getDisqualificationReason();
    } else {
      if (scoreDetails?.position) {
        flightPosition = scoreDetails?.position;
      }
      //a pre-flighted event places its teams in flights ahead of time, so one
      //without a flight is flagged for the organizer rather than left blank
      const flightName = flight
        ? `${flight?.name} Flight`
        : isUnflighted(flight)
        ? "No Flight Assignment"
        : "";
      if (flightName) {
        flightPosition += `${flightPosition}`.length > 0 ? ` / ${flightName}` : flightName;
      }
    }
    return {
      sectionItems: [
        {
          type: "secondary",
          value: flightPosition,
        },
      ]
    };
  };

  //an individual event's teams are the player, so the card names them. anywhere
  //else a team keeps its own name, even while it is down to a single player.
  const isIndividualEvent = currentTournament?.numberOfPlayers === 1;

  const teamColumn = {
    fit: "stretch",
    sections: isIndividualEvent ? [{ ...playerInfo }] : [{ ...contactInfo }],
  };

  if (
    getTeamFlight(shownTeam, flights) ||
    scoreDetails?.position ||
    currentTournament?.preflight
  ) {
    teamColumn.sections.push(getFlightPositionInfo());
  }

  const teamActions = {
    style: { justifyContent: "flex-end", maxWidth: "50px" },
    sections: [
      {
        sectionItems: [
          {
            type: "primary",
            value: (
              <GSButton
                buttonIcon={swappable ? faRetweet : faCircleXmark}
                onClick={(e) => {
                  e.stopPropagation();
                  return swappable ? swapTeam() : unassignTeam();
                }}
              />
            ),
          },
        ],
      },
    ],
  };

  const getGroup = () => {
    let group = [{ ...teamColumn }];
    if (!readOnly && ((assigned && !selected) || swappable)) {
      group.push({ ...teamActions });
    }
    return group;
  };

  let className = "";
  if (assigned) {
    className = "assigned";
  }
  if (available) {
    className += " available";
  }
  if (selected) {
    className += " selected";
  }
  if (justUnassigned) {
    className += " just-unassigned";
  }
  if (justAssigned) {
    className += " just-assigned";
  }
  if (isMoving) {
    className += " moving";
  }

  //the card only settles in once, so the mark comes off when it does
  const clearRecentMove = () => {
    dispatch(clearRecentlyMovedTeam(teamID));
  };
  return (
    <tournament-round-hole-assignment-team-item
      class={className}
      style={{
        ...(isPhone() && !assigned ? {minWidth: "300px"} : {}),
        ...(isMoving ? {position: "relative"} : {}),
      }}
      onAnimationEnd={
        justUnassigned || justAssigned ? clearRecentMove : undefined
      }
    >
      {isMoving && (
        <GSLoadingSpinnerOverlay
          spinnerSize="medium"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: "inherit",
          }}
        />
      )}
      <GSInfoGroup
        //fades the team behind the spinner without tinting the card itself
        style={isMoving ? {opacity: 0.5} : {}}
        listType="horizontal"
        dataGroups={getGroup()}
      ></GSInfoGroup>
    </tournament-round-hole-assignment-team-item>
  );
};

export default TournamentRoundHoleAssignmentTeamItem;
